import i18n from "@/core/plugins/vue-i18n";

const LTR_FONT_STACK = "Poppins, Helvetica, sans-serif";
const RTL_FONT_STACK = "'Almarai', 'Open Sans', sans-serif";

// Backend endpoint + localStorage keys. Endpoint/shape mirror iqh-frontend
// (shared backend): GET returns { translations: {en, ar}, languages,
// last_updated_time }.
const TRANSLATIONS_ENDPOINT = "/languages/translations";
const LS_TRANSLATIONS = "translation_data";
const LS_LANGUAGES = "languages_list";
const LS_LAST_UPDATED = "last_updated_time";

const safeParse = (value, fallback) => {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const STATIC_LANGUAGES = [
  { abbreviation: "en", name: "English", rtl: false },
  { abbreviation: "ar", name: "Arabic", rtl: true },
];

const i18nService = {
  defaultLanguage: "en",

  /**
   * Supported languages. Seeded from the static list, replaced by the
   * server-driven list once fetched (cached in localStorage). Shape:
   * `{ abbreviation, name, rtl }`.
   *
   * @type {Array<{abbreviation: string, name: string, rtl: boolean}>}
   */
  languageslist: safeParse(localStorage.getItem(LS_LANGUAGES), null) || [
    ...STATIC_LANGUAGES,
  ],

  /**
   * Backwards-compatibility alias kept for any callers that still read
   * `i18nService.languages`.
   */
  get languages() {
    return this.languageslist;
  },

  /**
   * Persist the language and apply every UI side effect (i18n locale,
   * `<html dir>`, `<html lang>`, font stack). Mirrors the
   * `iqh-frontend` service so module migrations port cleanly.
   *
   * @param {string} lang
   */
  setActiveLanguage(lang = this.getActiveLanguage()) {
    localStorage.setItem("language", lang);
    if (i18n) {
      i18n.locale = lang;
    }
    this.setDocumentLang(lang);
    this.setDocumentDirectionPerLocale(lang);
  },

  /**
   * @returns {string}
   */
  getActiveLanguage() {
    return localStorage.getItem("language") || this.defaultLanguage;
  },

  /**
   * @param {string} [lang]
   * @returns {boolean}
   */
  isRTL(lang = this.getActiveLanguage()) {
    const entry = this.languageslist.find((l) => l.abbreviation === lang);
    return !!(entry && entry.rtl);
  },

  setDocumentLang(lang = this.getActiveLanguage()) {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
  },

  setDocumentDirectionPerLocale(lang = this.getActiveLanguage()) {
    if (typeof document === "undefined") return;
    const rtl = this.isRTL(lang);
    document.dir = rtl ? "rtl" : "ltr";
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.style.setProperty(
      "--main-font",
      rtl ? RTL_FONT_STACK : LTR_FONT_STACK
    );
  },

  /**
   * Cached backend translations, keyed by locale.
   * @returns {Object<string, Object>}
   */
  getCachedTranslations() {
    return safeParse(localStorage.getItem(LS_TRANSLATIONS), {});
  },

  /**
   * Layer the cached backend translations ON TOP of the bundled locale JSON.
   * `mergeLocaleMessage` deep-merges, so a backend value overrides the bundled
   * one for the same key while any key the backend omits keeps its bundled
   * fallback. Bundled JSON therefore stays the safety net.
   */
  applyCachedTranslations() {
    const translations = this.getCachedTranslations();
    Object.keys(translations).forEach((locale) => {
      const messages = translations[locale];
      if (messages && typeof messages === "object") {
        i18n.mergeLocaleMessage(locale, messages);
      }
    });
  },

  /**
   * Fetch translations + languages from the backend, cache them, and merge
   * them over the bundled fallback. Mirrors the iqh-frontend contract.
   * @returns {Promise<void>}
   */
  async fetchLanguageDataFromServer() {
    try {
      const res = await fetch(
        process.env.VUE_APP_BASE_URL + TRANSLATIONS_ENDPOINT,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const data = await res.json();
      if (data?.translations) {
        localStorage.setItem(
          LS_TRANSLATIONS,
          JSON.stringify(data.translations)
        );
      }
      if (data?.languages) {
        localStorage.setItem(LS_LANGUAGES, JSON.stringify(data.languages));
        this.languageslist = data.languages;
      }
      if (data?.last_updated_time != null) {
        localStorage.setItem(
          LS_LAST_UPDATED,
          JSON.stringify(data.last_updated_time)
        );
      }
      this.applyCachedTranslations();
    } catch (e) {
      // Network/parse failure: keep whatever we already have (bundled + cache).
    }
  },

  /**
   * Apply cached translations at startup and, if nothing is cached yet, kick
   * off a first fetch. Safe to call before the app mounts.
   */
  initTranslations() {
    this.applyCachedTranslations();
    if (!localStorage.getItem(LS_TRANSLATIONS)) {
      // Fire-and-forget: bundled JSON renders immediately, backend copy merges
      // in once it arrives.
      this.fetchLanguageDataFromServer().then(() => {
        this.setActiveLanguage(this.getActiveLanguage());
      });
    }
  },
};

i18nService.initTranslations();

export default i18nService;
